import React, {
	createContext,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';
import {
	type IContextChildren,
	type IContextRef,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';
import {
	useArrowFocusTrap,
	type UseArrowFocusTrapParams,
} from './useArrowFocusTrap';

export type ArrowFocusTrapContextType = ReturnType<typeof useArrowFocusTrap>;

export const ArrowFocusTrapContext = createContext<ArrowFocusTrapContextType>({
	activeDescendantValue: '',
	disabled: false,
	activeItemIndex: -1,
	setActiveItemIndex: () => {},
	keysNavigationDisabled: false,
	setKeysNavigationDisabled: () => {},
	isFocusTrapActive: false,
	handleItemClick: () => () => {},
	handleItemInteractStart: () => () => {},
	setUserDisabled: () => {},
	setClickKeysDisabled: () => {},
	handleContainerBlur: () => {},
	handleContainerFocus: () => {},
});

export const useArrowFocusTrapProvider = () => {
	const context = useContext(ArrowFocusTrapContext);
	if (context === undefined) {
		throw new Error(
			'`useArrowFocusTrapProvider` must be used within a `ArrowFocusTrapProvider`',
		);
	}
	return context;
};

export type ArrowFocusTrapProviderRef = IContextRef<ArrowFocusTrapContextType>;

export type ArrowFocusTrapProviderProps = UseArrowFocusTrapParams & {
	ref?: ArrowFocusTrapProviderRef;
	children: IContextChildren<ArrowFocusTrapContextType>;
};

const ArrowFocusTrapProviderInner = (
	{children, ...props}: ArrowFocusTrapProviderProps,
	ref: ArrowFocusTrapProviderRef,
) => {
	const arrowFocusTrap = useArrowFocusTrap(props);

	const context = useMemo(() => arrowFocusTrap, [arrowFocusTrap]);

	useImperativeHandle(ref, () => context, [context]);

	return (
		<ArrowFocusTrapContext.Provider value={context}>
			{renderChildrenOrChildrenFunction(children, context)}
		</ArrowFocusTrapContext.Provider>
	);
};

export const ArrowFocusTrapProviderPrimitive = React.forwardRef<
	ArrowFocusTrapContextType,
	ArrowFocusTrapProviderProps
>(ArrowFocusTrapProviderInner);

/**
 * Arrow focus trap context provider
 * @example
 * <ArrowFocusTrapProvider ref={arrowFocusTrapRef}>
 * 	...
 * </ArrowFocusTrapProvider>
 * @example
 * <ArrowFocusTrapProvider ref={arrowFocusTrapRef}>
 * 	{(group) => (
 * 		...
 * 	)}
 * </ArrowFocusTrapProvider>
 */
export const ArrowFocusTrapProvider = ArrowFocusTrapProviderPrimitive;
